import { Box, BoxProps, useMediaQuery } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Photos, Videos } from '../../../components/Media';
import PersonalInfo from '../../../components/Profile/PersonalInfo';
import ProfileSidebar from '../../../components/Profile/ProfileSidebar/ProfileSidebar';
import SportBackgroundsSection from '../../../components/Profile/SportBackgroundsSection';
import WorkExperiencesSection from '../../../components/Profile/WorkExperiencesSection';
import SidebarLayout from '../../../layouts/SidebarLayout';
import { useThemeContext } from '../../../theme/ThemeContextProvider';
import { useGetMeQuery } from '../../../store/apiSlice/userApi';
import { IUserSportBackgroundResponse } from '../../../interfaces/user-sport-background.interface';
import { GetWorkExperienceRes } from '../../../interfaces/user-work-experience.interface';
import VerificationNotifyModal from './components/modals/VerificationNotifyModal';
import { selectAuthData } from '../../../store/selectors/authSelector';

const ProfilePage = () => {
  const { theme } = useThemeContext();
  const mobileView = useMediaQuery(theme.breakpoints.down('lg'));
  const location = useLocation();
  const navigate = useNavigate();
  const authData = useSelector(selectAuthData);

  const personalInfoRef = useRef<HTMLDivElement | null>(null);
  const photosRef = useRef<HTMLDivElement | null>(null);
  const videosRef = useRef<HTMLDivElement | null>(null);
  const sportBackgroundRef = useRef<HTMLDivElement | null>(null);
  const workExperienceRef = useRef<HTMLDivElement | null>(null);

  const [isDataLoaded, setIsDataLoaded] = useState({
    personalInfo: false,
    photos: false,
    videos: false,
    sportBackground: false,
    workExperience: false,
  });

  // Load current user once for the whole profile settings page, with all
  // relations needed by the sections (personal info, education, sport, work).
  const { data: meUser } = useGetMeQuery([
    'educations',
    'userSportBackgrounds',
    'workExperiences',
  ]);

  const mobileProps: BoxProps = mobileView
    ? {
        flexDirection: 'column',
        marginTop: { xs: '-20px', md: '-24px' },
      }
    : {};

  const loadData = async (section: string) => {
    await new Promise((resolve) => setTimeout(resolve, 500));
    setIsDataLoaded((prev) => ({ ...prev, [section]: true }));
  };

  useEffect(() => {
    loadData('personalInfo');
    loadData('photos');
    loadData('videos');
    loadData('sportBackground');
    loadData('workExperience');
  }, []);

  useEffect(() => {
    const scrollToSection = () => {
      if (
        location.hash === '#personal-info' &&
        personalInfoRef.current &&
        isDataLoaded.personalInfo
      ) {
        personalInfoRef.current.scrollIntoView({ behavior: 'smooth' });
      } else if (
        location.hash === '#photos' &&
        photosRef.current &&
        isDataLoaded.photos
      ) {
        photosRef.current.scrollIntoView({ behavior: 'smooth' });
      } else if (
        location.hash === '#videos' &&
        videosRef.current &&
        isDataLoaded.videos
      ) {
        videosRef.current.scrollIntoView({ behavior: 'smooth' });
      } else if (
        location.hash === '#sport-background' &&
        sportBackgroundRef.current &&
        isDataLoaded.sportBackground
      ) {
        sportBackgroundRef.current.scrollIntoView({ behavior: 'smooth' });
      } else if (
        location.hash === '#work-experience' &&
        workExperienceRef.current &&
        isDataLoaded.workExperience
      ) {
        workExperienceRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    };

    scrollToSection();
  }, [location, isDataLoaded]);

  const sections = [
    {
      id: 'personal-info',
      component: <PersonalInfo user={meUser} />,
      ref: personalInfoRef,
    },
    {
      id: 'sport-background',
      component: (
        <SportBackgroundsSection
          sportBackgrounds={
            meUser?.userSportBackgrounds as
              | IUserSportBackgroundResponse[]
              | undefined
          }
        />
      ),
      ref: sportBackgroundRef,
    },
    {
      id: 'work-experience',
      component: (
        <WorkExperiencesSection
          workExperiences={
            meUser?.workExperiences as GetWorkExperienceRes[] | undefined
          }
        />
      ),
      ref: workExperienceRef,
    },
    { id: 'photos', component: <Photos />, ref: photosRef },
    { id: 'videos', component: <Videos />, ref: videosRef },
  ];

  return (
    <>
      <VerificationNotifyModal
        isOpen={location?.state?.showUnverifiedUserModal}
        role={authData?.user?.role}
        onClose={() => {
          navigate(location.pathname, {
            state: { showUnverifiedUserModal: false },
          });
        }}
      />
      <SidebarLayout sidebar={<ProfileSidebar />} {...mobileProps}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flexGrow: 1,
            gap: '1.375rem',
          }}
        >
          {sections.map(({ id, component, ref }) => (
            <Box key={id}>
              <Box
                id={id}
                ref={ref}
                sx={{
                  display: 'block',
                  position: 'relative',
                  top: '-4.5rem',
                  visibility: 'hidden',
                }}
              />
              {component}
            </Box>
          ))}
        </Box>
      </SidebarLayout>
    </>
  );
};

export default ProfilePage;
